// SPDX-License-Identifier: AGPL-3.0-or-later

export const SCARE_OCCLUDER_ATTR = 'data-holiday-scare-dim';

/**
 * Marks the wrappers *between* a faded node and the message list.
 *
 * Fading a message, embed, or attachment is not enough on its own: the solid surface behind it
 * usually belongs to a layout wrapper further up (embed frames, attachment containers, group rows),
 * which keeps painting its own background and hides the sprite no matter how transparent the
 * content in front of it gets.
 */
export const SCARE_CLEAR_ATTR = 'data-holiday-scare-clear';

/** Which entrance the sprite is playing, so the fade can match it. */
export const SCARE_MODE_ATTR = 'data-holiday-scare-mode';

const CHAT_CONTAINER_SELECTOR = '[data-flx="channel.channel-chat-layout.container"]';
const MESSAGES_SELECTOR = '[data-flx="channel.channel-chat-layout.messages-area"]';

/** Real media. Anything containing one of these fades as media, not as text. */
const MEDIA_SELECTOR = 'img, video, canvas, iframe';

/**
 * Fade targets, widest first. `data-search-highlight-scope` marks every text-bearing part of a
 * message — body, system copy, embeds — and none of the row chrome. Dividers are separate elements
 * and are listed explicitly so date separators and unread bars fade with everything else.
 */
const OCCLUDER_SELECTOR = [
	'[data-search-highlight-scope="message"]',
	'[data-flx="channel.divider.container"]',
	'[data-flx^="channel.divider.unread-container"]',
	'[data-flx="channel.messages.new-messages-bar"]',
	MEDIA_SELECTOR,
].join(', ');

/**
 * Below this, media is an inline emoji, a reaction image, or an avatar. Fading those to the media
 * opacity while the text around them stays much lighter reads as a glitch.
 */
const MEDIA_MIN_PX = 64;

function intersects(a: DOMRect, b: DOMRect): boolean {
	return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

function isMediaSized(box: DOMRect): boolean {
	return box.width >= MEDIA_MIN_PX && box.height >= MEDIA_MIN_PX;
}

/**
 * Walks ancestors instead of testing `contains` against every accepted node, which was
 * quadratic on a full message window. Stops at the message list because every accepted
 * node is a strict descendant of it.
 */
function hasAcceptedAncestor(node: Element, accepted: ReadonlySet<Element>, messages: Element): boolean {
	for (let parent = node.parentElement; parent != null && parent !== messages; parent = parent.parentElement) {
		if (accepted.has(parent)) return true;
	}
	return false;
}

/** A node fades as media if it is, or wraps, a picture-sized image/video/embed frame. */
function holdsMedia(node: Element): boolean {
	if (node.matches(MEDIA_SELECTOR) && isMediaSized(node.getBoundingClientRect())) return true;
	for (const media of node.querySelectorAll(MEDIA_SELECTOR)) {
		if (isMediaSized(media.getBoundingClientRect())) return true;
	}
	return false;
}

/**
 * Fades message content sitting in front of a scare so the sprite reads through it.
 *
 * `box` must be the sprite's settled rectangle, not a mid-animation one — the sprite scales up from
 * nearly nothing, so measuring the element itself early would aim the fade at the wrong strip.
 * Boxes are snapshotted once: a scare is brief and does not follow scrolling.
 * Returns an undo that must run on despawn, including when the scare is cut short.
 */
export interface ScareOccluderTiming {
	/** Sprite animation length, so the fade is the inverse of the grow rather than a fixed ramp. */
	durationMs: number;
	/** Negative for a scare that is already part-way through, matching the sprite's resume. */
	delayMs: number;
	mode: 'grow' | 'pop';
}

export function dimScareOccluders(sprite: Element, box?: DOMRect, timing?: ScareOccluderTiming): () => void {
	const scope = sprite.closest(CHAT_CONTAINER_SELECTOR);
	const messages = scope?.querySelector(MESSAGES_SELECTOR);
	if (messages == null) return () => undefined;

	const spriteBox = box ?? sprite.getBoundingClientRect();
	if (spriteBox.width === 0 || spriteBox.height === 0) return () => undefined;

	// Measure first, mutate second. Every write below invalidates layout, so interleaving them
	// with getBoundingClientRect made each accepted node force a fresh reflow of the message list.
	const accepted = new Set<Element>();
	const plan: Array<{node: Element; kind: 'media' | 'text'}> = [];
	for (const node of messages.querySelectorAll(OCCLUDER_SELECTOR)) {
		// Outermost match wins, so an embed fades as one card instead of card-plus-contents.
		// querySelectorAll yields document order, so an accepted ancestor is already in the set.
		if (hasAcceptedAncestor(node, accepted, messages)) continue;
		const nodeBox = node.getBoundingClientRect();
		if (nodeBox.width === 0 || nodeBox.height === 0) continue;
		if (!intersects(nodeBox, spriteBox)) continue;
		if (node.matches(MEDIA_SELECTOR) && !isMediaSized(nodeBox)) continue;
		plan.push({node, kind: holdsMedia(node) ? 'media' : 'text'});
		accepted.add(node);
	}

	const dimmed: Array<Element> = [];
	const cleared: Array<Element> = [];
	for (const {node, kind} of plan) {
		node.setAttribute(SCARE_OCCLUDER_ATTR, kind);
		if (timing != null && node instanceof HTMLElement) {
			node.setAttribute(SCARE_MODE_ATTR, timing.mode);
			node.style.setProperty('--scare-duration', `${timing.durationMs}ms`);
			node.style.setProperty('--scare-delay', `${timing.delayMs}ms`);
		}
		dimmed.push(node);

		// Strip the surfaces the faded node sits on, up to the message list itself.
		for (let parent = node.parentElement; parent != null && parent !== messages; parent = parent.parentElement) {
			if (parent.hasAttribute(SCARE_CLEAR_ATTR)) break;
			parent.setAttribute(SCARE_CLEAR_ATTR, '');
			cleared.push(parent);
		}
	}

	return () => {
		for (const node of dimmed) {
			node.removeAttribute(SCARE_OCCLUDER_ATTR);
			node.removeAttribute(SCARE_MODE_ATTR);
			if (node instanceof HTMLElement) {
				node.style.removeProperty('--scare-duration');
				node.style.removeProperty('--scare-delay');
			}
		}
		for (const node of cleared) node.removeAttribute(SCARE_CLEAR_ATTR);
	};
}
